/**
 * LogoUploader.jsx — iter 51.
 *
 * Reusable square logo uploader for any entity that has `logo_file_id`.
 * Uses the public images endpoint (`/files/{id}/public`) for previews.
 *
 * Props:
 *   logoFileId   — current saved file id (or null)
 *   accent       — color for the upload button
 *   onUploaded   — async (file_id) => void; parent persists via PATCH
 *   testidPrefix — base for data-testid attrs
 *   label        — text above the box (defaults to "Logo")
 */
import React, { useRef, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { UploadSimple, Trash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LogoUploader = ({ logoFileId, accent = '#10b981', onUploaded, testidPrefix = 'logo', label = 'Logo' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please pick an image (PNG/JPG/SVG/WebP)'); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error('Logo must be under 4 MB'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/files/upload', fd);
      await onUploaded(r.data.file_id);
      toast.success('Logo updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`${testidPrefix}-uploader`}>
      <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-xl border border-border bg-background flex items-center justify-center overflow-hidden flex-shrink-0">
          {logoFileId ? (
            <img src={`${API}/files/${logoFileId}/public`} alt="" className="w-full h-full object-cover" data-testid={`${testidPrefix}-preview`} />
          ) : (
            <span className="text-[10px] uppercase tracking-wider font-bold text-text-muted">No logo</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`${testidPrefix}-input`} onChange={(e) => handleFile(e.target.files?.[0])} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testidPrefix}-upload-btn`}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
            <UploadSimple size={11} weight="bold" /> {busy ? 'Uploading…' : (logoFileId ? 'Replace logo' : 'Upload logo')}
          </button>
          {logoFileId && (
            <button type="button" onClick={async () => { setBusy(true); try { await onUploaded(null); toast.success('Logo removed'); } catch (e) { toast.error('Failed'); } finally { setBusy(false); } }}
              data-testid={`${testidPrefix}-remove-btn`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-danger border border-border">
              <Trash size={10} weight="bold" /> Remove
            </button>
          )}
          <p className="text-[10px] text-text-muted">PNG/JPG/SVG, ideally square, &le; 4 MB.</p>
        </div>
      </div>
    </div>
  );
};

export default LogoUploader;
